import { useCallback, useEffect, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Shield, MessageCircle, Loader2, Phone } from "lucide-react";
import { adminLogin, fetchAdminLeads, fetchAdminDealers, fetchAdminStats, buildWhatsAppLink } from "../lib/api";

const TOKEN_KEY = "rsn_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [stats, setStats] = useState({ leads: 0, dealer_enquiries: 0 });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setDealers([]);
  }, []);

  const load = useCallback(async (tk) => {
    try {
      const [l, d, s] = await Promise.all([fetchAdminLeads(tk), fetchAdminDealers(tk), fetchAdminStats(tk)]);
      setLeads(l);
      setDealers(d);
      setStats(s);
    } catch {
      toast.error("Session expired. Please login again.");
      logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={onLogin} className="w-full max-w-md bg-white border-2 border-[#0F172A] rounded-sm p-8 space-y-4">
          <div className="flex items-center gap-2"><Shield className="w-6 h-6 text-[#FF5A00]" /> <span className="label-tag text-[#FF5A00]">Admin Access</span></div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-[#0F172A]">Lead Dashboard</h1>
          <p className="text-sm text-[#4B5563]">Sign-in to view captured leads and dealer applications.</p>
          <div>
            <Label className="label-tag" htmlFor="pw">Password</Label>
            <Input
              id="pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 rounded-sm h-11"
              data-testid="admin-password"
              required
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-[#FF5A00] hover:bg-[#E65100] font-bold" data-testid="admin-login-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-tag text-[#FF5A00]">// Dashboard</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter text-[#0F172A] mt-2">Leads & Enquiries</h1>
        </div>
        <Button onClick={logout} variant="outline" className="rounded-sm border-2 border-[#0F172A]" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] rounded-sm overflow-hidden mb-10">
        <div className="bg-white p-6">
          <div className="label-tag">Total Leads</div>
          <div className="font-display text-4xl font-black text-[#0F172A] mt-2">{stats.leads}</div>
        </div>
        <div className="bg-white p-6">
          <div className="label-tag">Dealer Applications</div>
          <div className="font-display text-4xl font-black text-[#FF5A00] mt-2">{stats.dealer_enquiries}</div>
        </div>
        <div className="bg-white p-6">
          <div className="label-tag">Latest activity</div>
          <div className="font-display text-base font-bold text-[#0F172A] mt-2">
            {leads[0] ? new Date(leads[0].created_at).toLocaleString() : "—"}
          </div>
        </div>
      </div>

      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="rounded-sm border-2 border-[#0F172A] p-1 bg-white">
          <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white">Quote Leads</TabsTrigger>
          <TabsTrigger value="dealers" data-testid="tab-dealers" className="rounded-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white">Dealer Enquiries</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F5] text-[#4B5563]">
                <tr>
                  <th className="text-left px-4 py-3 label-tag">Date</th>
                  <th className="text-left px-4 py-3 label-tag">Product</th>
                  <th className="text-left px-4 py-3 label-tag">Qty</th>
                  <th className="text-left px-4 py-3 label-tag">Mobile</th>
                  <th className="text-left px-4 py-3 label-tag">Description</th>
                  <th className="text-right px-4 py-3 label-tag">Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-[#4B5563]">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-[#4B5563]">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-bold text-[#0F172A]">{l.product_name}</td>
                    <td className="px-4 py-3">{l.quantity}</td>
                    <td className="px-4 py-3 font-mono">{l.mobile}</td>
                    <td className="px-4 py-3 text-[#4B5563] max-w-xs truncate">{l.description || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={buildWhatsAppLink({ productName: l.product_name, productSlug: l.product_slug, quantity: l.quantity, mobile: l.mobile, description: l.description })}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#25D366] font-bold text-xs uppercase tracking-wider"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="dealers" className="mt-6">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F5] text-[#4B5563]">
                <tr>
                  <th className="text-left px-4 py-3 label-tag">Date</th>
                  <th className="text-left px-4 py-3 label-tag">Name</th>
                  <th className="text-left px-4 py-3 label-tag">Company</th>
                  <th className="text-left px-4 py-3 label-tag">Mobile</th>
                  <th className="text-left px-4 py-3 label-tag">Location</th>
                  <th className="text-left px-4 py-3 label-tag">Message</th>
                </tr>
              </thead>
              <tbody>
                {dealers.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-[#4B5563]">No applications yet.</td></tr>
                )}
                {dealers.map((d) => (
                  <tr key={d.id} className="border-t border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`dealer-row-${d.id}`}>
                    <td className="px-4 py-3 text-[#4B5563]">{new Date(d.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-bold text-[#0F172A]">{d.name}</td>
                    <td className="px-4 py-3">{d.company || "—"}</td>
                    <td className="px-4 py-3 font-mono"><a href={`tel:${d.mobile}`} className="inline-flex items-center gap-1 hover:text-[#FF5A00]"><Phone className="w-3 h-3" />{d.mobile}</a></td>
                    <td className="px-4 py-3">{d.location || "—"}</td>
                    <td className="px-4 py-3 text-[#4B5563] max-w-xs truncate">{d.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
